import { useState } from 'react';
import { Database, Network, SlidersHorizontal } from 'lucide-react';
import type { ViewSettings } from '@bits2schema/shared';
import { defaultViewSettings, erDiagramViewSettings } from '@bits2schema/shared';
import { useT } from '../i18n';
import { ContextMenu } from './ContextMenu';

interface ViewSettingsPanelProps {
  viewSettings: ViewSettings;
  onChange: (next: ViewSettings) => void;
}

/**
 * Epic 8 — přepínání pohledů. Popover u tlačítka „Zobrazení" (top-right
 * panel canvasu, vedle „Auto-layout") se dvěma rychlými presety (E8-US1) a
 * šesti nezávislými přepínači (E8-US2/US3/US4) mimo ně — kombinace, která
 * neodpovídá žádnému presetu, je plně platná (epic-8 kap. 7, E8-US2 AC3).
 * Mutace jde přes `updateViewSettings` v `store.tsx`, mimo undo/redo
 * zásobník (viz hlavička toho souboru) — vždy okamžitě persistuje celý
 * nový objekt najednou, nikdy dílčí `execute()` na přepínač.
 */
export function ViewSettingsPanel({ viewSettings, onChange }: ViewSettingsPanelProps) {
  const t = useT();
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);

  const toggle = (key: keyof ViewSettings) => onChange({ ...viewSettings, [key]: !viewSettings[key] });

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchor({ x: rect.left, y: rect.bottom + 4 });
        }}
      >
        <SlidersHorizontal size={16} />
        {t('canvas.viewSettings.button')}
      </button>

      {anchor && (
        <ContextMenu position={anchor} onClose={() => setAnchor(null)}>
          <div className="view-settings-panel">
            <div className="view-settings-panel__presets">
              <button type="button" onClick={() => onChange(defaultViewSettings())}>
                <Database size={16} />
                {t('canvas.viewSettings.presetDbSchema')}
              </button>
              <button type="button" onClick={() => onChange(erDiagramViewSettings())}>
                <Network size={16} />
                {t('canvas.viewSettings.presetErDiagram')}
              </button>
            </div>
            <label className="view-settings-panel__toggle">
              <input
                type="checkbox"
                checked={!viewSettings.showNonKeyColumns}
                onChange={() => toggle('showNonKeyColumns')}
              />
              {t('canvas.viewSettings.onlyKeyColumns')}
            </label>
            <label className="view-settings-panel__toggle">
              <input type="checkbox" checked={viewSettings.showColumnType} onChange={() => toggle('showColumnType')} />
              {t('canvas.viewSettings.showColumnType')}
            </label>
            <label className="view-settings-panel__toggle">
              <input type="checkbox" checked={viewSettings.showNullable} onChange={() => toggle('showNullable')} />
              {t('canvas.viewSettings.showNullable')}
            </label>
            <label className="view-settings-panel__toggle">
              <input type="checkbox" checked={viewSettings.showDefault} onChange={() => toggle('showDefault')} />
              {t('canvas.viewSettings.showDefault')}
            </label>
            <label className="view-settings-panel__toggle">
              <input type="checkbox" checked={viewSettings.showComment} onChange={() => toggle('showComment')} />
              {t('canvas.viewSettings.showComment')}
            </label>
            <label className="view-settings-panel__toggle">
              <input
                type="checkbox"
                checked={viewSettings.showCardinalityLabel}
                onChange={() => toggle('showCardinalityLabel')}
              />
              {t('canvas.viewSettings.showCardinalityLabel')}
            </label>
          </div>
        </ContextMenu>
      )}
    </>
  );
}
